import * as AgmNames from "./const/agm-names";
import { default as CallbackRegistryFactory, CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Glue42 } from "../../glue";
import { Glue42Core } from "@glue42/core";
import { OnGDShutdownMethodName } from "./const/agm-names";

function promisify(promise: Promise<any>, successCallback: (arg?: any) => void, errorCallback: (err?: any) => void) {
    const isFunction = (arg: any) => {
        return !!(arg && arg.constructor && arg.call && arg.apply);
    };

    if (!isFunction(successCallback) && !isFunction(errorCallback)) {
        return promise;
    }

    if (!isFunction(successCallback)) {
        successCallback = () => {
            // EMPTY
        };
    } else if (!isFunction(errorCallback)) {
        errorCallback = () => {
            // EMPTY
        };
    }

    return promise.then(successCallback, errorCallback);
}

export default class EntitlementsImpl implements Glue42.AppManager.Entitlements {

    private _registry: CallbackRegistry = CallbackRegistryFactory();
    private _isMethodRegistered = false;

    constructor(
        private _agm: Glue42Core.AGM.API
    ) {
        //
    }

    public handleBranchModified = (branch: Glue42.AppManager.Branch) => {
        this._registry.execute("branchChanged", branch);
    }

    public handleBranchesModified = (branches: Glue42.AppManager.Branch[]) => {
        this._registry.execute("branchesChanged", branches);
    }

    public getRegion = (success?: (region: string) => any, error?: (err: any) => any): void | Promise<string> => {
        return promisify(
            this._agmInvoke(
                AgmNames.GetConfigurationRegionMethodName,
                (e: Glue42Core.AGM.InvocationResult<{ Region: string }>) => e.returned.Region
            ),
            success,
            error
        );
    }

    public getBranches = (success?: (branches: Glue42.AppManager.Branch[]) => any, error?: (err: any) => any): void | Promise<Glue42.AppManager.Branch[]> => {
        const promise = this._agmInvoke(
            AgmNames.GetBranchesMethodName,
            (e) => {
                const obj = e.returned.Branches as { [key: string]: Glue42.AppManager.Branch };
                return Object.keys(obj).map((key) => obj[key]);
            }
        );

        return promisify(promise, success, error);
    }

    public getCurrentBranch = (success?: (branch: Glue42.AppManager.Branch) => any, error?: (err: any) => any): void | Promise<Glue42.AppManager.Branch> => {
        const promise = this._agmInvoke(
            AgmNames.GetCurrentBranchMethodName,
            (e) => e.returned.Branch,
            undefined
        );

        return promisify(promise, success, error);
    }

    public setRegion = (region: string, success?: (resMsg: string) => any, error?: (err: any) => any): void | Promise<string> => {
        const promise = this._agmInvoke(
            AgmNames.SetConfigurationRegionMethodName,
            (e) => e.returned.ResultMessage,
            { Region: region }
        );

        return promisify(promise, success, error);
    }

    public setCurrentBranch = (branch: string, success?: (resMsg: string) => any, error?: (err: any) => any): void | Promise<string> => {
        const promise = this._agmInvoke(
            AgmNames.SetCurrentBranchMethodName,
            (e) => e.returned.ResultMessage,
            { Branch: branch }
        );

        return promisify(promise, success, error);
    }

    public currentUser = (success?: (user: Glue42.AppManager.UserInfo) => any, error?: (err: any) => any): void | Promise<Glue42.AppManager.UserInfo> => {
        const promise = this._agmInvoke(AgmNames.GetUserMethodName);
        return promisify(promise, success, error);
    }

    public getFunctionalEntitlement = (funct: string, success?: (entitlement: string) => any, error?: (err: any) => any): void | Promise<string> => {
        const promise = this._agmInvoke(
            AgmNames.GetFunctionalEntitlementMethodName,
            (e) => e.returned.Entitlement,
            { Function: funct }
        );

        return promisify(promise, success, error);
    }

    public getFunctionalEntitlementBranch = (funct: string, branch: string, success?: (entitlement: string) => any, error?: (err: any) => any): void | Promise<string> => {
        const promise = this._agmInvoke(
            AgmNames.GetFunctionalEntitlementMethodName,
            (e) => e.returned.Entitlement,
            { Function: funct, Branch: branch }
        );
        return promisify(promise, success, error);
    }

    public canI = (func: string, success?: (allowed: boolean) => any, error?: (err: any) => any): void | Promise<boolean> => {
        const promise = this._agmInvoke(
            AgmNames.CanIMethodName,
            (e) => e.returned.Result,
            { Function: func }
        );

        return promisify(promise, success, error);
    }

    public canIBranch = (func: string, branch: string, success?: (allowed: boolean) => any, error?: (err: any) => any): void | Promise<boolean> => {
        const promise = this._agmInvoke(
            AgmNames.CanIMethodName,
            (e) => e.returned.Result,
            { Function: func, Branch: branch }
        );

        return promisify(promise, success, error);
    }

    public onBranchesChanged = (callback: (branches: Glue42.AppManager.Branch[]) => any): UnsubscribeFunction => {
        return this._registry.add("branchesChanged", callback);
    }

    public onBranchChanged = (callback: (branch: Glue42.AppManager.Branch) => any): UnsubscribeFunction => {
        return this._registry.add("branchChanged", callback);
    }

    public exit = (options?: Glue42.AppManager.ExitOpts): Promise<any> => {
        return this._agmInvoke(
            AgmNames.ShutdownMethodName,
            null,
            options
        );
    }

    public onShuttingDown = (callback: (args: Glue42.AppManager.ShuttingDownEventArgs) => Promise<{ prevent: boolean }>) => {
        this.registerMethod();
        return this._registry.add("onShuttingDown", callback);
    }

    public restart = (options?: Glue42.AppManager.ExitOpts): Promise<any> => {
        return this._agmInvoke(
            AgmNames.RestartMethodName,
            null,
            options
        );
    }

    private registerMethod() {
        if (!this._isMethodRegistered) {
            this._agm.register(OnGDShutdownMethodName, async (args) => {
                try {
                    const results = await Promise.all(this._registry.execute("onShuttingDown", args)) as { prevent: boolean }[];
                    const prevent = results.some((r) => r.prevent);
                    return { prevent };
                } catch (error) {
                    //
                }
            });
            this._isMethodRegistered = true;
        }
    }

    private _agmInvoke = (
        method: string,
        transformFunction?: (res: Glue42Core.AGM.InvocationResult<any>) => any,
        args?: object
    ): Promise<any> => {
        args = args || {};
        return new Promise((resolve, reject) => {
            const errHandler: Glue42Core.AGM.InvokeErrorHandler = (error) => reject(error);
            this._agm.invoke(method, args)
                .then((result) => {
                    if (!transformFunction) {
                        transformFunction = (d: Glue42Core.AGM.InvocationResult<any>) => d.returned;
                    }
                    resolve(transformFunction(result));
                })
                .catch(errHandler);
        });
    }
}
